import { Link } from "@tanstack/react-router";
import heroRecognitionAsset from "@/assets/caileve-hero-recognition.png.asset.json";
import giftVerdictAsset from "@/assets/caileve-gift-verdict.png.asset.json";
import collectionFlatlayAsset from "@/assets/caileve-collection-flatlay.png.asset.json";

const heroRecognition = heroRecognitionAsset.url;
const giftVerdict = giftVerdictAsset.url;
const collectionFlatlay = collectionFlatlayAsset.url;

const INK = "var(--caileve-ink)";
const PEARL = "var(--caileve-bg)";
const LILAC_BG = "var(--caileve-soft)";
const LILAC = "var(--caileve-lilac)";
const GOLD = "var(--caileve-gold)";
const MUTED = "var(--caileve-muted)";

const SERIF = "'Cormorant Garamond', Georgia, serif";

const eyebrowStyle: React.CSSProperties = {
  fontSize: 11,
  letterSpacing: "0.28em",
  textTransform: "uppercase",
  color: MUTED,
};

const CTA_HERO = "Start free — scan one piece before you buy";

export function LandingPage() {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        overflowY: "auto",
        WebkitOverflowScrolling: "touch",
        backgroundColor: PEARL,
        color: INK,
      }}
    >
      <TopNav />
      <Hero />
      <Recognition />
      <CaileveEye />
      <WholeTrip />
      <Archetype />
      <DailyLook />
      <GiftIntelligence />
      <Features />
      <YourCollection />
      <FromCollection />
      <StartFree />
      <ClosingCta />
      <CampaignStrip />
      <Footer />
    </div>
  );
}

function TopNav() {
  return (
    <nav
      className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between px-6 py-5"
    >
      <div
        className="uppercase"
        style={{
          fontFamily: SERIF,
          color: PEARL,
          letterSpacing: "4px",
          fontSize: 15,
        }}
      >
        Cailève
      </div>
      <div className="flex items-center gap-6">
        <div
          className="hidden md:flex items-center gap-6"
          style={{ fontFamily: SERIF, color: PEARL, fontSize: 14, letterSpacing: "1px" }}
        >
          <a href="#eye" className="hover:opacity-80">Eye</a>
          <a href="#style" className="hover:opacity-80">Style</a>
          <a href="#profile" className="hover:opacity-80">Profile</a>
          <a href="#pricing" className="hover:opacity-80">Pricing</a>
        </div>
        <Link
          to="/login"
          className="px-5 py-2 rounded-full hover:opacity-90 transition-opacity"
          style={{
            backgroundColor: GOLD,
            color: INK,
            fontFamily: SERIF,
            letterSpacing: "1.5px",
            fontSize: 13,
          }}
        >
          Start free
        </Link>
      </div>
    </nav>
  );
}

function Hero() {
  return (
    <section
      className="relative w-full min-h-[100svh] md:min-h-screen flex flex-col md:flex-row items-stretch"
      style={{ backgroundColor: INK }}
    >
      {/* Image */}
      <div className="relative w-full md:w-1/2 h-[42svh] md:h-auto md:min-h-screen overflow-hidden order-1 md:order-2">
        <img
          src={heroRecognition}
          alt="A woman in soft golden light examining a delicate gold pendant at her neckline — the moment of recognition."
          className="w-full h-full object-cover"
          style={{ objectPosition: "center 30%" }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 md:hidden"
          style={{
            background: `linear-gradient(to bottom, rgba(30,22,36,0) 55%, ${INK} 100%)`,
          }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 hidden md:block"
          style={{
            background: `linear-gradient(to right, rgba(30,22,36,0.6) 0%, rgba(30,22,36,0) 40%)`,
          }}
        />
      </div>

      {/* Copy */}
      <div className="relative w-full md:w-1/2 flex items-center justify-center order-2 md:order-1 px-6 py-10 md:py-16">
        <div className="max-w-lg w-full flex flex-col items-start text-left">
          <span style={eyebrowStyle}>Jewelry-first style intelligence</span>
          <h1
            className="mt-4"
            style={{
              fontFamily: SERIF,
              color: PEARL,
              fontSize: "clamp(48px, 9vw, 88px)",
              fontWeight: 400,
              lineHeight: 1.02,
              margin: 0,
            }}
          >
            Is this you?
          </h1>
          <p
            className="mt-2"
            style={{
              fontFamily: SERIF,
              fontStyle: "italic",
              color: GOLD,
              fontSize: "clamp(22px, 3.5vw, 30px)",
              lineHeight: 1.2,
              margin: 0,
            }}
          >
            Or just beautiful.
          </p>
          <div style={{ width: 60, height: 1, backgroundColor: GOLD, margin: "20px 0" }} />
          <p
            style={{
              fontFamily: SERIF,
              color: PEARL,
              fontSize: "clamp(15px, 2vw, 18px)",
              lineHeight: 1.55,
              margin: 0,
              opacity: 0.9,
            }}
          >
            Cailève Eye reads your collection and answers the question you already know how to ask. Before you buy. Before you regret.
          </p>
          <p
            style={{
              fontFamily: SERIF,
              color: PEARL,
              fontSize: "clamp(13px, 1.6vw, 15px)",
              lineHeight: 1.5,
              margin: "12px 0 0",
              opacity: 0.7,
              letterSpacing: "0.02em",
            }}
          >
            Style what you own. Check what you are considering. Save what works.
          </p>
          <Link
            to="/login"
            className="mt-6 px-7 py-3 rounded-full hover:opacity-90 transition-opacity inline-block"
            style={{
              backgroundColor: GOLD,
              color: INK,
              fontFamily: SERIF,
              letterSpacing: "1.5px",
              fontSize: 14,
            }}
          >
            {CTA_HERO}
          </Link>
          <p
            className="mt-3"
            style={{
              fontFamily: SERIF,
              fontStyle: "italic",
              color: MUTED,
              fontSize: 13,
              margin: "12px 0 0",
            }}
          >
            No credit card required.
          </p>
        </div>
      </div>
    </section>
  );
}

function Recognition() {
  return (
    <section className="px-6 py-24 md:py-32 flex justify-center" style={{ backgroundColor: PEARL }}>
      <div
        className="max-w-2xl text-center"
        style={{
          fontFamily: SERIF,
          color: INK,
          fontSize: "clamp(22px, 3vw, 30px)",
          lineHeight: 1.5,
          fontWeight: 400,
        }}
      >
        <p style={{ margin: 0 }}>
          You have stood in a store holding something beautiful and known, immediately, that it wasn't you.
        </p>
        <p style={{ margin: "1.2em 0 0" }}>
          Not because it was wrong. Because you were right.
        </p>
        <p style={{ margin: "1.2em 0 0", fontStyle: "italic", color: MUTED }}>
          That instinct is not indecision.
        </p>
        <p style={{ margin: "0.4em 0 0", fontStyle: "italic", color: MUTED }}>
          It is the most precise thing you own.
        </p>
        <div style={{ width: 60, height: 1, backgroundColor: GOLD, margin: "32px auto" }} />
        <p style={{ margin: 0 }}>Cailève is the tool that holds it steady.</p>
      </div>
    </section>
  );
}

function SectionHeader({ label, headline }: { label: string; headline: React.ReactNode }) {
  return (
    <>
      <span style={eyebrowStyle}>{label}</span>
      <h2
        className="mt-4"
        style={{
          fontFamily: SERIF,
          color: INK,
          fontSize: "clamp(30px, 4.5vw, 44px)",
          fontWeight: 400,
          lineHeight: 1.2,
          margin: 0,
        }}
      >
        {headline}
      </h2>
    </>
  );
}

function SectionBody({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="mt-6"
      style={{
        fontFamily: SERIF,
        color: INK,
        opacity: 0.85,
        fontSize: "clamp(16px, 2vw, 19px)",
        lineHeight: 1.65,
        maxWidth: 640,
      }}
    >
      {children}
    </div>
  );
}

function QuietFlow({ items }: { items: string[] }) {
  return (
    <p
      className="mt-8"
      style={{
        fontFamily: SERIF,
        fontStyle: "italic",
        color: INK,
        opacity: 0.7,
        fontSize: "clamp(16px, 2vw, 20px)",
        lineHeight: 1.8,
        margin: "32px 0 0",
      }}
    >
      {items.map((item, i) => (
        <span key={item}>
          {item}
          {i < items.length - 1 && (
            <span aria-hidden style={{ margin: "0 0.6em", color: GOLD }}>·</span>
          )}
        </span>
      ))}
    </p>
  );
}

function CaileveEye() {
  return (
    <section id="eye" className="px-6 py-24 md:py-32" style={{ backgroundColor: LILAC_BG }}>
      <div className="max-w-3xl mx-auto">
        <SectionHeader label="Cailève Eye" headline={<>See a piece. Know if it's yours.</>} />
        <SectionBody>
          <p style={{ margin: 0 }}>
            Photograph anything — in a store, a magazine, a friend's wardrobe. Cailève reads the piece against your specific collection and delivers a personal verdict.
          </p>
          <p style={{ margin: "1em 0 0" }}>
            Not "this is nice." Whether it is you — or just beautiful.
          </p>
        </SectionBody>
        <QuietFlow
          items={[
            "This is you.",
            "This is something you've been missing.",
            "You already have something close.",
            "Worth considering — but not yet.",
            "This could be you — it's a stretch, but a supported one.",
          ]}
        />
        <p
          className="mt-8"
          style={{
            fontFamily: SERIF,
            fontStyle: "italic",
            color: MUTED,
            fontSize: 15,
            margin: "32px 0 0",
          }}
        >
          "Styled for warm rain." Cailève reads the weather too.
        </p>
      </div>
    </section>
  );
}

function WholeTrip() {
  return (
    <section id="style" className="px-6 py-24 md:py-32" style={{ backgroundColor: PEARL }}>
      <div className="max-w-3xl mx-auto">
        <SectionHeader label="The Whole Trip" headline={<>No other styling app does this.</>} />
        <SectionBody>
          <p style={{ margin: 0 }}>
            Every piece you scan in a shopping trip stays in context. Cailève reasons across everything you've considered — not just what's in your hand right now.
          </p>
          <p style={{ margin: "1em 0 0" }}>
            It knows you scanned something similar an hour ago. It manages the decision queue so you don't have to.
          </p>
        </SectionBody>
        <blockquote
          className="mt-10"
          style={{
            fontFamily: SERIF,
            fontStyle: "italic",
            color: INK,
            fontSize: "clamp(22px, 3vw, 28px)",
            borderLeft: `2px solid ${GOLD}`,
            paddingLeft: 24,
            margin: "40px 0 0",
            lineHeight: 1.4,
          }}
        >
          The intelligence travels with you through the store.
        </blockquote>
      </div>
    </section>
  );
}

function Archetype() {
  return (
    <section id="profile" className="px-6 py-24 md:py-32" style={{ backgroundColor: LILAC_BG }}>
      <div className="max-w-3xl mx-auto">
        <SectionHeader label="Your Style Archetype" headline={<>Your wardrobe already knows who you are.</>} />
        <SectionBody>
          <p style={{ margin: 0 }}>
            Cailève identifies your style archetype from the way you actually behave — the pieces you reach for, the ones you overlook, the feelings vocabulary your collection carries.
          </p>
          <p style={{ margin: "1em 0 0" }}>Not a personality quiz. A reading.</p>
        </SectionBody>
        <QuietFlow
          items={[
            "The Refined",
            "The Expressive",
            "The Fluent",
            "The Romantic",
            "The Curator",
            "The Edge",
            "The Adventurer",
          ]}
        />
        <p
          className="mt-8"
          style={{
            fontFamily: SERIF,
            fontStyle: "italic",
            color: MUTED,
            fontSize: 15,
            margin: "32px 0 0",
          }}
        >
          Your Style Brief updates as your collection evolves. It is a living signal, not a permanent label.
        </p>
      </div>
    </section>
  );
}

function DailyLook() {
  return (
    <section className="px-6 py-24 md:py-32" style={{ backgroundColor: PEARL }}>
      <div className="max-w-3xl mx-auto">
        <SectionHeader
          label="Today's Cailève Look"
          headline={
            <>
              It already knows what day it is.
              <br />
              And what you haven't worn recently.
            </>
          }
        />
        <SectionBody>
          <p style={{ margin: 0 }}>
            Every morning, Cailève assembles a complete look from your collection — adjusted for today's weather, your occasion, and the pieces that have been waiting longest.
          </p>
          <p style={{ margin: "1em 0 0" }}>The necklace you almost forgot you owned.</p>
          <p style={{ margin: "0.3em 0 0" }}>Built into the look you needed today.</p>
        </SectionBody>
        <p
          className="mt-8"
          style={{
            fontFamily: SERIF,
            fontStyle: "italic",
            color: MUTED,
            fontSize: 15,
            margin: "32px 0 0",
          }}
        >
          The decision is already made. You just have to get dressed.
        </p>
      </div>
    </section>
  );
}

function GiftIntelligence() {
  return (
    <section className="px-6 py-24 md:py-32" style={{ backgroundColor: LILAC_BG }}>
      <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 items-center">
        <div>
          <SectionHeader
            label="Gift Intelligence"
            headline={
              <>
                She sent a link.
                <br />
                He opened Cailève Eye.
                <br />
                It told him whether it was her.
              </>
            }
          />
          <SectionBody>
            <p style={{ margin: 0 }}>
              Generate a Style Profile token from your Cailève profile. Share it with anyone shopping for you.
            </p>
            <p style={{ margin: "1em 0 0" }}>
              They open Cailève Eye with your token active. The verdict arrives in your language — "This is her." — not theirs.
            </p>
            <p style={{ margin: "1em 0 0" }}>The relief of knowing. Before they buy.</p>
          </SectionBody>
          <p
            className="mt-8"
            style={{
              fontFamily: SERIF,
              fontStyle: "italic",
              color: MUTED,
              fontSize: 15,
              margin: "32px 0 0",
            }}
          >
            The most considered gift is one that already asked her wardrobe.
          </p>
        </div>
        <div className="w-full">
          <img
            src={giftVerdict}
            alt="A hand holds a phone showing the Cailève verdict 'This is her.' in serif type on a soft blush screen."
            className="w-full h-auto"
            style={{ borderRadius: 4, boxShadow: "0 20px 60px -20px rgba(30,22,36,0.35)" }}
          />
        </div>
      </div>
    </section>
  );
}

function Features() {
  const features = [
    {
      name: "Style Me",
      description:
        "Describe your moment — occasion, weather, mood. Cailève builds a complete look from your real collection.",
    },
    {
      name: "Wardrobe Fit",
      description:
        "Photograph any piece in a store. Cailève reads your collection and tells you whether it is you — before you buy.",
    },
    {
      name: "Final Touch",
      description:
        "Anchor what you are wearing. Scan the final piece. Cailève tells you whether it belongs with the look.",
    },
    {
      name: "Lookbook",
      description:
        "Save the looks, pairings, and decisions that work. Your wardrobe memory, building over time.",
    },
  ] as const;

  return (
    <section className="px-6 py-24 md:py-32" style={{ backgroundColor: INK }}>
      <div className="max-w-4xl mx-auto">
        <SectionHeader
          label="What Cailève helps you do"
          headline={<>Four ways to know<br />what belongs.</>}
        />
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-8">
          {features.map(({ name, description }) => (
            <div
              key={name}
              style={{
                borderTop: `1px solid rgba(198,166,107,0.25)`,
                paddingTop: 24,
              }}
            >
              <p
                style={{
                  fontFamily: SERIF,
                  color: GOLD,
                  fontSize: 11,
                  textTransform: "uppercase",
                  letterSpacing: "0.25em",
                  margin: 0,
                }}
              >
                {name}
              </p>
              <p
                style={{
                  fontFamily: SERIF,
                  color: PEARL,
                  fontSize: "clamp(15px, 2vw, 17px)",
                  lineHeight: 1.6,
                  marginTop: 10,
                  opacity: 0.85,
                }}
              >
                {description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function YourCollection() {
  return (
    <section className="px-6 py-24 md:py-32" style={{ backgroundColor: PEARL }}>
      <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 items-center">
        <div className="w-full order-2 md:order-1">
          <img
            src={collectionFlatlay}
            alt="A gold necklace, ring, and delicate earrings resting on ivory linen in soft daylight."
            className="w-full h-auto"
            style={{ borderRadius: 4, boxShadow: "0 20px 60px -20px rgba(30,22,36,0.35)" }}
          />
        </div>
        <div className="order-1 md:order-2">
          <SectionHeader label="Your Collection" headline={<>Your jewelry case is a self-portrait.</>} />
          <SectionBody>
            <p style={{ margin: 0 }}>
              The pieces you reach for every time. The ones you overlook. The ones that are most you — and have been waiting the longest.
            </p>
            <p style={{ margin: "1em 0 0" }}>
              Cailève reads those patterns and builds from them. Your wardrobe knows you better than you think.
            </p>
          </SectionBody>
          <QuietFlow
            items={[
              "Most Reached For",
              "Your Edit",
              "Rediscover",
              "Feelings Vocabulary",
              "Wear Logging",
              "Scan History",
            ]}
          />
        </div>
      </div>
    </section>
  );
}

function FromCollection() {
  return (
    <section className="px-6 py-24 md:py-32" style={{ backgroundColor: INK }}>
      <div className="max-w-3xl mx-auto text-center">
        <span style={{ ...eyebrowStyle, color: GOLD }}>From the Collection</span>
        <blockquote
          className="mt-8"
          style={{
            fontFamily: SERIF,
            fontStyle: "italic",
            color: PEARL,
            fontSize: "clamp(24px, 3.5vw, 34px)",
            lineHeight: 1.4,
            margin: "32px 0 0",
          }}
        >
          "She had her wedding shoes for years.
          <br />
          Style Me built her a look around them."
        </blockquote>
        <p
          className="mt-6"
          style={{
            fontFamily: SERIF,
            color: MUTED,
            fontSize: 14,
            letterSpacing: "0.1em",
            margin: "24px 0 0",
          }}
        >
          — Cailève tester, founding cohort
        </p>
      </div>
    </section>
  );
}

function TierCard({
  label,
  price,
  lines,
  cta,
  featured,
}: {
  label: string;
  price?: string;
  lines: string[];
  cta: string;
  featured?: boolean;
}) {
  return (
    <div
      className="p-8 md:p-10 flex flex-col"
      style={{
        backgroundColor: featured ? INK : PEARL,
        color: featured ? PEARL : INK,
        border: `1px solid ${GOLD}`,
        borderRadius: 4,
      }}
    >
      <div className="flex items-baseline justify-between">
        <span style={{ ...eyebrowStyle, color: featured ? GOLD : MUTED }}>{label}</span>
        {price && (
          <span
            style={{
              fontFamily: SERIF,
              fontSize: 22,
              color: featured ? GOLD : INK,
            }}
          >
            {price}
          </span>
        )}
      </div>
      <div
        className="mt-6 flex-1"
        style={{
          fontFamily: SERIF,
          fontSize: 17,
          lineHeight: 1.9,
          opacity: 0.9,
        }}
      >
        {lines.map((line) => (
          <div key={line}>{line}</div>
        ))}
      </div>
      <Link
        to={cta === "Become a Founding Member" ? "/plans" : "/login"}
        search={cta === "Become a Founding Member" ? { success: false, tier: undefined } : undefined}
        className="mt-8 px-6 py-3 rounded-full hover:opacity-90 transition-opacity text-center"
        style={{
          backgroundColor: GOLD,
          color: INK,
          fontFamily: SERIF,
          letterSpacing: "1.5px",
          fontSize: 14,
        }}
      >
        {cta}
      </Link>
    </div>
  );
}

function StartFree() {
  return (
    <section id="pricing" className="px-6 py-24 md:py-32" style={{ backgroundColor: LILAC_BG }}>
      <div className="max-w-5xl mx-auto">
        <div className="text-center">
          <span style={eyebrowStyle}>Start Free</span>
          <h2
            className="mt-4"
            style={{
              fontFamily: SERIF,
              color: INK,
              fontSize: "clamp(30px, 4.5vw, 44px)",
              fontWeight: 400,
              lineHeight: 1.2,
              margin: 0,
            }}
          >
            Let Cailève remember what works.
          </h2>
          <p
            style={{
              fontFamily: SERIF,
              fontStyle: "italic",
              color: MUTED,
              fontSize: "clamp(15px, 2vw, 18px)",
              marginTop: 12,
              lineHeight: 1.5,
            }}
          >
            Start with three Cailève Eye scans, on us. No credit card. No commitment.
          </p>
        </div>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
          <TierCard
            label="Free"
            lines={[
              "3 Cailève Eye scans included",
              "Style Me · Daily Look",
              "Collection (up to 20 pieces)",
              "Your Archetype · Your Edit",
            ]}
            cta={CTA_HERO}
          />
          <TierCard
            label="Founding Member"
            price="$6.99/mo"
            featured
            lines={[
              "Unlimited Cailève Eye scans — Wardrobe Fit and Final Touch",
              "Style Me · Daily Look · Gift Intelligence · The Whole Trip",
              "Unlimited collection. Unlimited looks.",
              "Your wardrobe memory, building over time.",
              "Founding Member rate — locked forever.",
            ]}
            cta="Become a Founding Member"
          />
        </div>
        <p
          className="mt-8 text-center"
          style={{
            fontFamily: SERIF,
            fontStyle: "italic",
            color: MUTED,
            fontSize: 14,
            maxWidth: 560,
            margin: "32px auto 0",
            lineHeight: 1.6,
          }}
        >
          Founding Member pricing is available for a limited time to the first users who shape what Cailève becomes.
        </p>
      </div>
    </section>
  );
}

function ClosingCta() {
  return (
    <section className="px-6 py-24 md:py-32 text-center" style={{ backgroundColor: INK }}>
      <div className="max-w-2xl mx-auto flex flex-col items-center">
        <h2
          style={{
            fontFamily: SERIF,
            color: PEARL,
            fontSize: "clamp(30px, 5vw, 48px)",
            fontWeight: 400,
            lineHeight: 1.2,
            margin: 0,
          }}
        >
          Before you buy it, ask your wardrobe.
        </h2>
        <p
          className="mt-6"
          style={{
            fontFamily: SERIF,
            fontStyle: "italic",
            color: MUTED,
            fontSize: "clamp(16px, 2vw, 20px)",
            margin: "24px 0 0",
          }}
        >
          Buy fewer regrets, not fewer beautiful things.
        </p>
        <Link
          to="/login"
          className="mt-10 px-8 py-3 rounded-full hover:opacity-90 transition-opacity inline-block"
          style={{
            backgroundColor: GOLD,
            color: INK,
            fontFamily: SERIF,
            letterSpacing: "1.5px",
            fontSize: 14,
          }}
        >
          {CTA_HERO}
        </Link>
        <p
          className="mt-4"
          style={{
            fontFamily: SERIF,
            fontStyle: "italic",
            color: MUTED,
            fontSize: 14,
          }}
        >
          Trust your taste again.
        </p>
      </div>
    </section>
  );
}

function CampaignStrip() {
  return (
    <div
      className="px-6 py-5 text-center"
      style={{ backgroundColor: "rgba(198,166,107,0.08)", borderTop: `1px solid rgba(198,166,107,0.2)` }}
    >
      <p
        style={{
          fontFamily: SERIF,
          fontStyle: "italic",
          color: MUTED,
          fontSize: 13,
          margin: 0,
          lineHeight: 1.6,
        }}
      >
        18 women are already building their style memory with Cailève.
      </p>
    </div>
  );
}

function Footer() {
  return (
    <footer className="px-6 py-12" style={{ backgroundColor: PEARL, borderTop: `1px solid ${GOLD}` }}>
      <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center md:items-start justify-between gap-6 text-center md:text-left">
        <div>
          <div
            className="uppercase"
            style={{ fontFamily: SERIF, color: INK, letterSpacing: "4px", fontSize: 16 }}
          >
            Cailève
          </div>
          <div
            className="mt-2"
            style={{ fontFamily: SERIF, fontStyle: "italic", color: MUTED, fontSize: 14 }}
          >
            Jewelry-first style intelligence.
          </div>
        </div>
        <div className="flex items-center gap-6" style={{ fontSize: 13, color: MUTED }}>
          <a
            href="https://www.instagram.com/caileveapp/"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: MUTED }}
          >
            Instagram
          </a>
          <a
            href="https://www.pinterest.com/myCaileve/"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: MUTED }}
          >
            Pinterest
          </a>
        </div>
        <div className="flex items-center gap-6" style={{ fontSize: 13 }}>
          <Link to="/privacy" style={{ color: MUTED }}>
            Privacy Policy
          </Link>
          <Link to="/terms" style={{ color: MUTED }}>
            Terms of Service
          </Link>
        </div>
      </div>
      <div className="mt-8 text-center" style={{ color: MUTED, fontSize: 11 }}>
        © 2026 Écrin Style LLC. All rights reserved.
      </div>
    </footer>
  );
}
